// The ONE set of XOP social links shown in EVERY email footer — campaign AND
// transactional — plus an Outlook-safe renderer for the "follow us" call-to-
// action block. Single source of truth so every template (masthead / webinar /
// newsletter / case-study …) and every customer email show the SAME block; a
// campaign author never edits these per-email.
//
// XOP keeps exactly TWO channels here — LinkedIn and YouTube (Facebook was
// retired 2026-07-10) — rendered as bold, solid CALL-TO-ACTION BUTTONS under a
// short "let's stay connected" invite. The ask (from the CEO) is to grow the
// LinkedIn following, so this is a real CTA, not tiny gray icons. Buttons are
// bulletproof/table-based with `bgcolor` on the cell, so they render as solid
// blocks in Outlook (Word engine) too — border-radius degrades to a square
// button there, never a broken icon glyph.
//
// Mirrored (same links, same block) by two Ruby copies — change all three
// together:
//   * crm-api    → apps/crm-api/app/lib/email_social_links.rb    (campaign + transactional sends)
//   * portal-api → apps/portal-api/app/lib/email_social_links.rb (customer / support sends)

export interface EmailSocialLink {
  label: string
  url: string
  // Solid button background — the platform's own brand color.
  bg: string
  // Button call-to-action label.
  cta: string
}

// Order matches how they read top-to-bottom in the footer.
export const EMAIL_SOCIAL_LINKS: readonly EmailSocialLink[] = [
  { label: "LinkedIn", url: "https://www.linkedin.com/company/xopnetworks/", bg: "#0A66C2", cta: "Follow us on LinkedIn" },
  { label: "YouTube", url: "https://www.youtube.com/@XopnetworksDallas", bg: "#FF0000", cta: "Subscribe on YouTube" },
] as const

export const EMAIL_SOCIAL_HEADLINE = "Let's stay connected"
export const EMAIL_SOCIAL_SUBTEXT =
  "Come say hi and follow XOP Networks for product news, live webinars, and the latest in emergency communications."

interface SocialFooterOptions {
  // "dark" (light text, for a dark footer) or "light" (dark text, for a light
  // footer). Default "dark". Buttons keep their platform colors either way.
  theme?: "dark" | "light"
  // Extra inline styles for the wrapping <div> (top/bottom margin etc.).
  marginStyle?: string
}

// A centered, Outlook-safe "follow us" block: a short invite headline + subline,
// then the two social call-to-action buttons stacked and centered. Drop it into
// a `text-align:center` footer cell. The wrapping `.xop-social` class lets a
// dark-mode-aware template (e.g. portal-api's layout) re-light the text.
export function socialFooterLinksHtml(opts: SocialFooterOptions = {}): string {
  const dark = opts.theme !== "light"
  const headlineColor = dark ? "#ffffff" : "#1a202c"
  const subtextColor = dark ? "#aab4c8" : "#6b7280"
  const margin = opts.marginStyle || "margin:0;"
  const buttons = EMAIL_SOCIAL_LINKS.map(socialButtonHtml).join("\n")
  return `<div class="xop-social" style="${margin} text-align:center; font-family:Arial,Helvetica,sans-serif;">
  <p style="margin:0 0 6px 0; font-size:17px; line-height:24px; font-weight:bold; color:${headlineColor};">${EMAIL_SOCIAL_HEADLINE}</p>
  <p style="margin:0 0 18px 0; font-size:13px; line-height:20px; color:${subtextColor};">${EMAIL_SOCIAL_SUBTEXT}</p>
  ${buttons}
</div>`
}

// One bulletproof, solid social button — `bgcolor` on the cell so Outlook fills
// it (border-radius degrades to a square block, which is fine). `min-width`
// keeps the two buttons the same width in modern clients.
function socialButtonHtml(s: EmailSocialLink): string {
  return `<table role="presentation" border="0" cellspacing="0" cellpadding="0" align="center" style="margin:0 auto 10px auto;">
    <tr>
      <td align="center" bgcolor="${s.bg}" style="background-color:${s.bg}; border-radius:6px;">
        <a href="${s.url}" target="_blank" style="display:inline-block; min-width:200px; padding:12px 22px; text-align:center; font-family:Arial,Helvetica,sans-serif; font-size:14px; font-weight:bold; color:#ffffff; text-decoration:none;">${s.cta}</a>
      </td>
    </tr>
  </table>`
}
